import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import MelbiVial from "@/components/MelbiVial";
import { TREATMENTS } from "@/lib/treatments";
import { goToProductIntake, PRODUCT_INTAKE_URLS } from "@/lib/intake";
import useSEO from "@/lib/useSEO";

const listRows = TREATMENTS.filter((t) => !t.hiddenFromList).flatMap((t) =>
  t.homeCards
    ? t.homeCards.map((c) => ({ ...t, ...c }))
    : [
        t.cardPricing
          ? {
              ...t,
              cardKey: t.slug,
              treatment: t.cardEyebrow,
              name: t.cardTitle || t.name,
              pricing: t.cardPricing,
              dualVialOnCard: true,
            }
          : { ...t, cardKey: t.slug },
      ]
);

// Line-by-line horizontal row selector for /treatments.
// Deliberately no eyebrow / headline / description — the list itself is the page.

export default function TreatmentsList() {
  const navigate = useNavigate();
  useSEO({
    title: "Treatments | Melbi Wellness",
    description:
      "Browse Melbi's clinician-guided wellness treatments including weight management, NAD+, Sermorelin, Glutathione, MIC + B12, B12, and topical peptide skin care support.",
    url: "https://melbiwellness.com/treatments",
  });

  return (
    <div data-testid="treatments-list-page" className="bg-[#FAF6EF] min-h-[70vh]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 md:pt-14 md:pb-20">
        <ul
          data-testid="treatments-list"
          className="border-t border-[#E8E5DF]"
        >
          {listRows.map((t) => {
            const hasLink = Boolean(PRODUCT_INTAKE_URLS[t.slug]);
            const price = t?.pricing?.startingAt;
            return (
              <li key={t.cardKey} className="border-b border-[#E8E5DF]">
                <button
                  type="button"
                  data-testid={`treatments-list-card-${t.cardKey}`}
                  onClick={() =>
                    hasLink
                      ? goToProductIntake(t.slug)
                      : navigate(t.href || `/treatments/${t.slug}`)
                  }
                  aria-label={
                    hasLink
                      ? `Start intake for ${t.name}`
                      : `View ${t.name}`
                  }
                  className="group w-full text-left flex items-center gap-3 sm:gap-5 py-3.5 sm:py-4 px-2 sm:px-3 -mx-2 sm:-mx-3 rounded-xl hover:bg-white transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C96A4E]/40"
                >
                  {/* Vial thumb */}
                  <span className={`shrink-0 flex items-center justify-center ${t.dualVialOnCard && t.secondaryVialSlug ? "w-16 sm:w-20 h-12 sm:h-14 -space-x-3" : "w-12 h-12 sm:w-14 sm:h-14"}`}>
                    <MelbiVial
                      slug={t.primaryVialSlug || t.slug}
                      alt=""
                      testId={`row-vial-${t.cardKey}`}
                      className="h-full w-auto"
                    />
                    {t.dualVialOnCard && t.secondaryVialSlug && (
                      <MelbiVial
                        slug={t.secondaryVialSlug}
                        alt={t.secondaryVialAlt || ""}
                        testId={`row-vial-secondary-${t.slug}`}
                        className="h-full w-auto"
                      />
                    )}
                  </span>

                  {/* Text */}
                  <span className="flex-1 min-w-0">
                    <span
                      data-testid={`treatments-list-product-${t.cardKey}`}
                      className="block text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-[#8A9A92]"
                    >
                      {t.treatment}
                    </span>
                    <h2
                      data-testid={`treatments-list-name-${t.cardKey}`}
                      className="mt-0.5 font-display text-base sm:text-lg md:text-xl font-semibold tracking-tight text-[#2C2B29] leading-tight truncate"
                    >
                      {t.name}
                    </h2>
                    {price && (
                      <span
                        data-testid={`treatments-list-price-${t.cardKey}`}
                        className="mt-1 block text-xs sm:text-[13px] leading-snug"
                      >
                        <span className="font-semibold text-[#C96A4E]">
                          ${price}
                        </span>
                        <span className="font-semibold text-[#2C2B29]">/month</span>
                        {t?.pricing?.cardSuffix !== "" && (
                          <span className="block font-normal text-[#8A9A92] text-[10px] sm:text-[11px]">
                            {t.cardPricing ? t.pricing.cardSuffix : "3-month plan"}
                          </span>
                        )}
                      </span>
                    )}
                    {t.medDetails?.inline && (
                      <span
                        data-testid={`treatments-list-concentration-${t.slug}`}
                        className="mt-0.5 block text-[11px] sm:text-xs text-[#5E5C58] leading-snug"
                      >
                        {t.medDetails.inline}
                      </span>
                    )}
                  </span>

                  {/* CTA */}
                  <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-transparent group-hover:bg-[#C96A4E] group-hover:text-white text-[#C96A4E] border border-[#C96A4E]/40 group-hover:border-[#C96A4E] px-3.5 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold transition-colors">
                    <span className="hidden sm:inline">
                      {hasLink ? "Start Now" : "View"}
                    </span>
                    <span className="sm:hidden">
                      {hasLink ? "Start" : "View"}
                    </span>
                    <ArrowRight
                      size={14}
                      className="transition-transform group-hover:translate-x-0.5"
                    />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
